// Saved, Done (passport/scrapbook), Friends, Profile screens.

import React from 'react';
import { HeroArt, Stars, Icon, GenerativeAvatar, FriendAvatar, Constellation } from './components.jsx';
import { distLabel } from './lib/geo.js';
import { useHomeBase } from './context.js';
import { enablePush, pushSupported, pushEnabled } from './lib/push.js';
import { trackEvent } from './lib/analytics.js';
import { installAvailable, isStandalone, onInstallAvailabilityChange, promptInstall } from './lib/install.js';
import {
  larkleIsAuthed, larkleGetUid, larkleGetUser, larkleGetApp,
  apiFetchFriends, apiFetchIncomingRequests, apiFetchOutgoingRequests,
  apiSendFriendRequest, apiRespondToRequest, apiCancelFriendRequest, apiRemoveFriend,
  apiSearchUser, apiClaimHandle, apiCheckHandle, apiGenerateInviteCode, apiGetProfile,
  apiFetchPlans, apiCreatePlan, apiRsvpToPlan, apiCancelPlan, apiLeavePlan, apiFetchLocation, placeToActivity,
  apiFetchShareInbox, apiMarkSharesRead, apiStartConversation,
} from './lib/api.js';
import { ChatsSection, StartChatPanel } from './chat.jsx';

function SavedRowSkeleton() {
  return (
    <div style={{
      background: 'var(--paper)', borderRadius: 22,
      padding: 10, marginBottom: 12,
      display: 'flex', gap: 12, alignItems: 'stretch',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        width: 96, height: 108, flexShrink: 0, borderRadius: 14,
        background: 'var(--bg-tint)',
        animation: 'larkle-shimmer 1.4s ease-in-out infinite',
      }}/>
      <div className="col" style={{ flex: 1, gap: 8, justifyContent: 'center' }}>
        {[['50%', 0], ['80%', 0.1], ['65%', 0.2]].map(([w, delay], i) => (
          <div key={i} style={{
            height: i === 0 ? 11 : i === 1 ? 17 : 13,
            width: w, borderRadius: 6,
            background: 'var(--bg)',
            animation: `larkle-shimmer 1.4s ease-in-out ${delay}s infinite`,
          }}/>
        ))}
      </div>
    </div>
  );
}

// One surface for "my places": the maybe-list (queued up) and the passport
// (been there), as two segments. Stamping a place moves it from one to the
// other — same motion the server makes (Saved → Liked).
export function SavedScreen({ segment = 'maybe', onSegmentChange, savedActivities, loading, error, onRetry, filters, onQueryChange, onToggleNearMe, onOpen, onMarkDone, onShare, onRemove, onLoadMore, loadingMore, doneEntries = [], doneActivities = [], doneLoading = false, onOpenEntry }) {
  const scrollRef = React.useRef(null);

  const handleScroll = React.useCallback(() => {
    if (!onLoadMore) return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) onLoadMore();
  }, [onLoadMore]);

  const nearMeActive = !!filters?.coords;
  const hasActiveFilter = !!(filters?.query || nearMeActive);
  const isBeen = segment === 'been';
  // Only count entries that actually resolve to a place — an entry whose place lookup
  // fails (deleted place, sync gap) is dropped from the grid below, so the stats must
  // be computed from the same resolvable set or "Stamped" overstates the visible cards.
  const stampedEntries = doneEntries
    .map(entry => ({ entry, activity: doneActivities.find(x => x.id === entry.id) }))
    .filter(({ activity }) => !!activity);
  const avg = stampedEntries.length
    ? (stampedEntries.reduce((s, { entry }) => s + (entry.stars || 0), 0) / stampedEntries.length).toFixed(1)
    : null;

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <div className="hdr">
        <div>
          <div className="eyebrow">
            {isBeen
              ? (stampedEntries.length ? `${stampedEntries.length} stamped · the story so far`
                 : doneLoading ? 'loading…' : 'the story so far')
              : (loading ? 'loading…' : `${savedActivities.length} saved · queued up`)}
          </div>
          <h1 style={{ whiteSpace: 'nowrap' }}>
            {isBeen
              ? <>The <span className="serif" style={{ color: 'var(--terracotta)' }}>passport</span></>
              : <>Your <span className="serif" style={{ color: 'var(--terracotta)' }}>maybe-list</span></>}
          </h1>
        </div>
      </div>

      {/* Maybe / Been segments */}
      <div style={{ padding: '0 22px 8px' }}>
        <div style={{ display: 'inline-flex', background: 'rgba(42,26,26,0.06)', borderRadius: 999, padding: 3 }}>
          {[
            { id: 'maybe', label: 'Maybe-list' },
            { id: 'been',  label: 'Been there' },
          ].map(t => (
            <button key={t.id} onClick={() => onSegmentChange(t.id)} style={{
              border: 0, background: segment === t.id ? 'var(--paper)' : 'transparent',
              borderRadius: 999, padding: '6px 14px', fontSize: 12.5, fontWeight: 500,
              color: segment === t.id ? 'var(--ink)' : 'var(--ink-mute)',
              boxShadow: segment === t.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {isBeen ? (
        <div key="been" className="scroll seg-in" style={{ padding: '4px 22px 24px' }}>
          {/* stats strip */}
          <div style={{
            background: 'linear-gradient(135deg, oklch(0.50 0.13 18), oklch(0.42 0.14 358))',
            borderRadius: 22, padding: 16, color: 'var(--paper)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 18, position: 'relative', overflow: 'hidden',
          }}>
            <div className="col">
              <div className="upper" style={{ fontSize: 10, opacity: 0.7, fontWeight: 600 }}>Stamped</div>
              <div className="serif" style={{ fontSize: 32, lineHeight: 1 }}>{stampedEntries.length}</div>
            </div>
            <div className="col">
              <div className="upper" style={{ fontSize: 10, opacity: 0.7, fontWeight: 600 }}>Avg rating</div>
              <div className="row" style={{ gap: 6 }}>
                <div className="serif" style={{ fontSize: 32, lineHeight: 1 }}>{avg ?? '-'}</div>
                <Stars value={Math.round(Number(avg) || 0)} size={14}/>
              </div>
            </div>
            <div className="col" style={{ alignItems: 'flex-end' }}>
              <div className="upper" style={{ fontSize: 10, opacity: 0.7, fontWeight: 600 }}>This month</div>
              <div className="serif" style={{ fontSize: 32, lineHeight: 1 }}>
                {stampedEntries.filter(({ entry: e }) => {
                  const d = e.whenTs ? new Date(e.whenTs) : null;
                  const now = new Date();
                  return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                }).length}
              </div>
            </div>
            <svg width={120} height={120} viewBox="0 0 120 120" style={{ position: 'absolute', right: -20, top: -30, opacity: 0.18 }}>
              <circle cx={60} cy={60} r={50} fill="none" stroke="white" strokeWidth={1.5} strokeDasharray="3 4"/>
              <circle cx={60} cy={60} r={36} fill="none" stroke="white" strokeWidth={1}/>
            </svg>
          </div>

          {doneLoading && stampedEntries.length === 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[1, 2, 3, 4].map(i => <StampCardSkeleton key={i}/>)}
            </div>
          ) : stampedEntries.length === 0 ? (
            <div style={{ padding: '28px 20px 40px', textAlign: 'center' }}>
              {/* an empty stamp waiting for its first press */}
              <div style={{
                width: 120, height: 120, margin: '0 auto 18px', borderRadius: '50%',
                border: '2.5px dashed rgba(200,75,49,0.45)', transform: 'rotate(-8deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span className="upper" style={{
                  fontSize: 9, fontWeight: 700, color: 'var(--terracotta)', opacity: 0.75,
                  fontFamily: 'JetBrains Mono, monospace', textAlign: 'center', lineHeight: 1.6,
                }}>your first<br/>stamp goes<br/>here</span>
              </div>
              <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
                No stamps yet.
              </div>
              <p className="soft" style={{ fontSize: 14, marginBottom: 16 }}>
                Do something from your maybe-list, rate it, and it lands here forever.
              </p>
              <button className="btn ghost" style={{ height: 42, padding: '0 22px' }}
                      onClick={() => onSegmentChange('maybe')}>
                Browse the maybe-list
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {stampedEntries.map(({ entry, activity }, i) => (
                <div key={entry.id} style={{ animation: `stamp-in 0.34s ${Math.min(i, 7) * 0.05}s cubic-bezier(.2,.8,.3,1) backwards` }}>
                  <StampCard activity={activity} entry={entry} onClick={() => onOpenEntry(entry.id)}/>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* Filter bar */}
      <div style={{ padding: '0 16px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            width: 14, height: 14, color: 'var(--ink-mute)', pointerEvents: 'none',
          }}>
            <svg viewBox="0 0 24 24" fill="none"><circle cx={10.5} cy={10.5} r={6.5} stroke="currentColor" strokeWidth={1.8}/><path d="M15.5 15.5L20 20" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"/></svg>
          </span>
          <input
            value={filters?.query ?? ''}
            onChange={e => onQueryChange(e.target.value)}
            placeholder="Search saved places…"
            aria-label="Search saved places"
            style={{
              width: '100%', height: 36, boxSizing: 'border-box',
              border: '0.5px solid var(--hair)', borderRadius: 10,
              padding: '0 12px 0 32px',
              fontFamily: 'inherit', fontSize: 13.5,
              background: 'var(--paper)', outline: 'none', color: 'var(--ink)',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onToggleNearMe} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            height: 28, padding: '0 11px', borderRadius: 999,
            border: nearMeActive ? 0 : '0.5px solid var(--hair)',
            background: nearMeActive ? 'var(--ink)' : 'var(--paper)',
            color: nearMeActive ? 'var(--paper)' : 'var(--ink-mute)',
            fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
          }}>
            <span style={{ width: 12, height: 12 }}>{Icon.pin}</span>
            Near me
          </button>
        </div>
      </div>

      <div key="maybe" ref={scrollRef} className="scroll seg-in" onScroll={handleScroll} style={{ padding: '4px 16px 24px' }}>
        {loading ? (
          [1, 2, 3].map(i => <SavedRowSkeleton key={i}/>)
        ) : error && savedActivities.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div className="serif" style={{ fontSize: 28, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
              {error === 'offline' ? "You're offline" : "Couldn't load your list"}
            </div>
            <p className="soft" style={{ fontSize: 14, marginBottom: 14 }}>
              {error === 'offline' ? 'Check your connection and try again.' : 'Something went wrong talking to the server.'}
            </p>
            <button className="btn coral" style={{ height: 42, padding: '0 24px' }} onClick={onRetry}>Try again</button>
          </div>
        ) : savedActivities.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div className="serif" style={{ fontSize: 28, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
              {hasActiveFilter ? 'No matches' : 'Nothing saved yet'}
            </div>
            <p className="soft" style={{ fontSize: 14 }}>
              {hasActiveFilter ? 'Try a different search or remove the filter.' : 'Swipe right on Discover and they\'ll pile up here.'}
            </p>
          </div>
        ) : (
          <>
            {savedActivities.map(a => (
              <SavedRow key={a.id} activity={a}
                        onOpen={() => onOpen(a.id)}
                        onMarkDone={() => onMarkDone(a.id)}
                        onShare={() => onShare(a.id)}
                        onRemove={() => onRemove(a.id)}/>
            ))}
            {loadingMore && [1, 2].map(i => <SavedRowSkeleton key={`more-${i}`}/>)}
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function SavedRow({ activity, onOpen, onMarkDone, onShare, onRemove }) {
  const base = useHomeBase();
  const [confirming, setConfirming] = React.useState(false);
  return (
    <div style={{
      background: 'var(--paper)', borderRadius: 22, marginBottom: 14,
      overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      cursor: 'pointer',
    }} onClick={onOpen}>

      {/* Photo */}
      <div style={{ position: 'relative', height: 152 }}>
        <HeroArt activity={activity} height={152} rounded={0} showNav={false}/>
      </div>

      {/* Info */}
      <div style={{ padding: '13px 16px 0' }}>
        <div className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 3 }}>
          {activity.kind}{activity.address ? ` · ${activity.address}` : ''}
        </div>
        <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, color: 'var(--ink)', textWrap: 'pretty', marginBottom: 10 }}>
          {activity.name}
        </div>
        {activity.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {activity.tags.slice(0, 4).map(t => (
              <span key={t} className="chip" style={{ fontSize: 11, height: 24, padding: '0 10px' }}>{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="row between" style={{
        padding: '10px 16px 13px',
        borderTop: '0.5px solid var(--hair)',
        minHeight: 52,
      }}>
        {confirming ? (
          <>
            <span className="serif" style={{ fontSize: 14, color: 'var(--ink)' }}>Remove from saved?</span>
            <div className="row" style={{ gap: 6 }}>
              <button className="chip" style={{ height: 30, padding: '0 12px', fontSize: 12.5, cursor: 'pointer',
                        background: 'rgba(42,26,26,0.07)', color: 'var(--ink-mute)' }}
                      onClick={e => { e.stopPropagation(); setConfirming(false); }}>
                Keep
              </button>
              <button className="chip" style={{ height: 30, padding: '0 12px', fontSize: 12.5, cursor: 'pointer',
                        background: 'rgba(180,30,30,0.1)', color: '#b41e1e', border: 0 }}
                      onClick={e => { e.stopPropagation(); onRemove(); }}>
                Remove
              </button>
            </div>
          </>
        ) : (
          <>
            <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              {distLabel(activity, base)}
            </span>
            <div className="row" style={{ gap: 6 }}>
              <button className="iconbtn" style={{ width: 32, height: 32 }} aria-label="Share"
                      onClick={e => { e.stopPropagation(); onShare(); }}>
                <span style={{ width: 14, height: 14 }}>{Icon.share}</span>
              </button>
              <button className="iconbtn solid" style={{ width: 32, height: 32 }} aria-label="Mark done"
                      onClick={e => { e.stopPropagation(); onMarkDone(); }}>
                <span style={{ width: 14, height: 14 }}>{Icon.check}</span>
              </button>
              <button className="iconbtn" style={{ width: 32, height: 32 }} aria-label="Remove"
                      onClick={e => { e.stopPropagation(); setConfirming(true); }}>
                <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StampCard({ activity, entry, onClick }) {
  // rotate slightly for the scrapbook feel — seeded by id
  const tilt = ((activity.id.charCodeAt(0) + activity.id.charCodeAt(2)) % 5) - 2;
  return (
    <div onClick={onClick} style={{
      background: 'var(--paper)',
      borderRadius: 18,
      overflow: 'hidden',
      transform: `rotate(${tilt * 0.5}deg)`,
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      position: 'relative',
    }}>
      <div style={{ position: 'relative' }}>
        <HeroArt activity={activity} height={120} rounded={0} showNav={false}/>
        {/* stamped overlay */}
        <div style={{
          position: 'absolute', right: 8, top: 8,
          padding: '4px 10px', borderRadius: 8,
          border: '2px solid var(--terracotta)', color: 'var(--terracotta)',
          fontWeight: 700, fontSize: 9, letterSpacing: 0.12,
          transform: 'rotate(-6deg)',
          background: 'rgba(255,255,255,0.85)',
          fontFamily: 'JetBrains Mono, monospace',
        }}>DONE</div>
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <div className="serif" style={{ fontSize: 15, lineHeight: 1.15, color: 'var(--ink)', marginBottom: 4, textWrap: 'pretty' }}>
          {activity.name}
        </div>
        <div className="row between" style={{ marginTop: 4 }}>
          <Stars value={entry.stars} size={12}/>
          <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-mute)' }}>{entry.when}</span>
        </div>
      </div>
    </div>
  );
}

function StampCardSkeleton() {
  return (
    <div style={{ background: 'var(--paper)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ height: 120, background: 'var(--bg-tint)', animation: 'larkle-shimmer 1.4s ease-in-out infinite' }}/>
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ height: 15, width: '70%', borderRadius: 6, background: 'var(--bg-tint)', animation: 'larkle-shimmer 1.4s ease-in-out 0.1s infinite' }}/>
        <div style={{ height: 12, width: '40%', borderRadius: 6, background: 'var(--bg-tint)', animation: 'larkle-shimmer 1.4s ease-in-out 0.2s infinite' }}/>
      </div>
    </div>
  );
}

// ── Friends ────────────────────────────────────────────────────────────────
function FriendCardSkeleton() {
  return (
    <div style={{
      background: 'var(--paper)', borderRadius: 18, padding: 12,
      marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        background: 'var(--bg-tint)',
        animation: 'larkle-shimmer 1.4s ease-in-out infinite',
      }}/>
      <div className="col" style={{ flex: 1, gap: 6, justifyContent: 'center' }}>
        {[['52%', 0], ['36%', 0.12]].map(([w, delay], i) => (
          <div key={i} style={{
            height: i === 0 ? 14 : 11, width: w, borderRadius: 6,
            background: 'var(--bg)',
            animation: `larkle-shimmer 1.4s ease-in-out ${delay}s infinite`,
          }}/>
        ))}
      </div>
    </div>
  );
}

// ── Friend activity feed ───────────────────────────────────────────────────
// Entries are deck-compatible activities carrying `friendDid` — the same
// data a future swipeable friends card stack will consume.

function timeAgo(ts) {
  if (!ts) return '';
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 35) return `${Math.floor(days / 7)}w ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function FriendActivityCardSkeleton() {
  return (
    <div style={{
      background: 'var(--paper)', borderRadius: 20, marginBottom: 14,
      overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <div style={{ height: 118, background: 'var(--bg-tint)', animation: 'larkle-shimmer 1.4s ease-in-out infinite' }}/>
      <div style={{ padding: '14px 14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[['38%', 0], ['64%', 0.12], ['46%', 0.24]].map(([w, delay], i) => (
          <div key={i} style={{
            height: i === 1 ? 17 : 12, width: w, borderRadius: 6,
            background: 'var(--bg)',
            animation: `larkle-shimmer 1.4s ease-in-out ${delay}s infinite`,
          }}/>
        ))}
      </div>
    </div>
  );
}

// A postcard from a friend's night out: hero image with a rotated time-stamp
// (echoing the passport's DONE stamp), the friend's avatar overlapping the
// photo edge, their rating, the place, their one-line memory — and a
// bookmark so "they did it" becomes "I'm doing it" in one tap.
function FriendActivityCard({ activity, isSaved, onOpen, onSave }) {
  const f = activity.friendDid;
  const first = (f.name || f.handle || 'A friend').split(' ')[0];
  return (
    <div onClick={onOpen} style={{
      background: 'var(--paper)', borderRadius: 20, marginBottom: 14,
      overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      cursor: 'pointer',
    }}>
      <div style={{ position: 'relative', height: 118, borderRadius: '20px 20px 0 0', overflow: 'hidden' }}>
        <HeroArt activity={activity} height={118} rounded={0} showNav={false}/>
        {/* stamped relative time — same visual language as the passport */}
        <div style={{
          position: 'absolute', right: 10, top: 10,
          padding: '3px 9px', borderRadius: 7,
          border: '2px solid var(--terracotta)', color: 'var(--terracotta)',
          fontWeight: 700, fontSize: 9, letterSpacing: 0.1, textTransform: 'uppercase',
          transform: 'rotate(-5deg)',
          background: 'rgba(255,255,255,0.85)',
          fontFamily: 'JetBrains Mono, monospace',
        }}>{timeAgo(f.whenTs)}</div>
      </div>
      {/* avatar bridges image and text — clear authorship */}
      <div style={{
        position: 'relative', marginTop: -17, marginLeft: 14, width: 38, height: 38,
        borderRadius: '50%', boxShadow: '0 0 0 3px var(--paper)', background: 'var(--paper)',
      }}>
        <FriendAvatar friend={f} size={38}/>
      </div>
      <div style={{ padding: '6px 14px 12px' }}>
        <div className="row" style={{ gap: 7, marginBottom: 3 }}>
          <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
            <b style={{ color: 'var(--ink)' }}>{first}</b> was here
          </span>
          {f.stars > 0 && <Stars value={f.stars} size={11}/>}
        </div>
        <div className="row between" style={{ gap: 10, alignItems: 'flex-start' }}>
          <div className="col" style={{ gap: 4, minWidth: 0, flex: 1 }}>
            <span className="serif" style={{ fontSize: 20, lineHeight: 1.12, color: 'var(--ink)', textWrap: 'pretty' }}>
              {activity.name}
            </span>
            {f.note && (
              <span className="serif" style={{ fontSize: 14, lineHeight: 1.3, color: 'var(--ink-soft)', textWrap: 'pretty' }}>
                “{f.note}”
              </span>
            )}
            <span className="upper" style={{ fontSize: 9, color: 'var(--ink-mute)', fontWeight: 600, marginTop: 2 }}>
              {activity.kind}
            </span>
          </div>
          <button className={`iconbtn ${isSaved ? 'solid' : ''}`}
                  aria-label={isSaved ? 'Saved' : 'Save this place'}
                  style={{ width: 36, height: 36, flexShrink: 0, marginTop: 2 }}
                  onClick={e => { e.stopPropagation(); onSave(); }}>
            <span style={{ width: 15, height: 15 }}>{isSaved ? Icon.bookmarkFill : Icon.bookmark}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function FriendsScreen({ authUser, activities, friendsActivity, savedActivities = [], onSaveActivity, onOpenActivity, onOpenPlan, onOpenShareInbox, shareInbox, conversations, onOpenChat, onChatStarted, plans, setPlans, onToast, onGoToProfile }) {
  const [tab, setTab] = React.useState('feed');
  const [newChatOpen, setNewChatOpen] = React.useState(false);
  const [friends, setFriends] = React.useState([]);
  const [incomingReqs, setIncomingReqs] = React.useState([]);
  const [outgoingReqs, setOutgoingReqs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [addOpen, setAddOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const handleCacheKey = `larkle-handle:${larkleGetUid() || 'anon'}`;
  const [hasHandle, setHasHandle] = React.useState(() => {
    try { return !!localStorage.getItem(handleCacheKey); } catch { return false; }
  });

  // Friends only matters if people can find you back — nudge anyone without
  // a handle yet, since that's the #1 way requests come in.
  React.useEffect(() => {
    if (!larkleIsAuthed()) return;
    apiGetProfile()
      .then(p => {
        setHasHandle(!!p?.handle);
        if (p?.handle) { try { localStorage.setItem(handleCacheKey, p.handle); } catch {} }
      })
      .catch(() => {});
  }, [authUser]);

  // background=true skips the loading spinner — used for the poll/focus
  // refresh below so it doesn't flash the skeleton over an already-populated
  // list every 60s.
  const reload = React.useCallback((background = false) => {
    if (!larkleIsAuthed()) return;
    if (!background) setLoading(true);
    setError(null);
    Promise.all([apiFetchFriends(), apiFetchIncomingRequests(), apiFetchOutgoingRequests(), apiFetchPlans()])
      .then(([f, inc, out, p]) => {
        setFriends(f || []);
        setIncomingReqs(inc || []);
        setOutgoingReqs(out || []);
        setPlans((p || []).filter(pl => pl.status !== 'Cancelled' && pl.status !== 'Completed'));
      })
      .catch(err => { if (!background) setError(err?.offline ? 'offline' : 'error'); })
      .finally(() => { if (!background) setLoading(false); });
  }, [setPlans]);

  // Refetch on mount, whenever auth changes, and then keep polling in the
  // background — friend request/accept pushes land server-side (see
  // nudge-api) but this screen stays mounted for as long as the People tab
  // is open, so without a poll (same 60s + focus pattern used for
  // conversations in app.jsx) an incoming request's badge can update
  // elsewhere while this list sits stale, and an outgoing "pending" card
  // never flips to "friends" once the other side accepts.
  React.useEffect(() => {
    if (!authUser) return;
    reload();
    const iv = setInterval(() => reload(true), 60_000);
    const onFocus = () => reload(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [authUser, reload]);

  const respondToRequest = (reqId, accept) => {
    apiRespondToRequest(reqId, accept)
      .then(() => {
        setIncomingReqs(r => r.filter(req => req.id !== reqId));
        if (accept) { reload(); onToast('Friend added'); trackEvent('friend_request_accepted'); }
        else { onToast('Declined'); trackEvent('friend_request_declined'); }
      })
      .catch(() => onToast('Something went wrong'));
  };

  const cancelRequest = (reqId) => {
    apiCancelFriendRequest(reqId)
      .then(() => { setOutgoingReqs(r => r.filter(req => req.id !== reqId)); onToast('Request cancelled'); })
      .catch(() => onToast('Something went wrong'));
  };

  const removeFriend = (friendId) => {
    apiRemoveFriend(friendId)
      .then(() => { setFriends(f => f.filter(fr => fr.userId !== friendId)); onToast('Removed'); })
      .catch(() => onToast('Something went wrong'));
  };

  // DM a friend: get-or-create the thread (server dedupes) and jump in
  const messageFriend = (friendId) => {
    apiStartConversation([friendId])
      .then(convo => { trackEvent('chat_started'); onChatStarted(convo); })
      .catch(err => onToast(err?.offline ? "You're offline" : 'Could not open the chat'));
  };

  const rsvpPlan = (planId, rsvp) => {
    const before = plans;
    // optimistic: flip my rsvp immediately, roll back on failure
    const uid = larkleGetUid();
    setPlans(p => p.map(pl => pl.id === planId
      ? { ...pl, invitees: (pl.invitees || []).map(inv => inv.userId === uid ? { ...inv, rsvp } : inv) }
      : pl));
    apiRsvpToPlan(planId, rsvp)
      .then(updated => { setPlans(p => p.map(pl => pl.id === planId ? updated : pl)); trackEvent('plan_rsvp', { rsvp }); })
      .catch(() => { setPlans(before); onToast('Could not update RSVP'); });
  };

  const dismissPlan = (plan) => {
    const isHost = plan.organizerId === larkleGetUid();
    (isHost ? apiCancelPlan(plan.id) : apiLeavePlan(plan.id))
      .then(() => { setPlans(p => p.filter(pl => pl.id !== plan.id)); onToast(isHost ? 'Plan cancelled' : 'Left plan'); })
      .catch(() => onToast('Something went wrong'));
  };

  const isAuthed = larkleIsAuthed();
  const unreadShares = (shareInbox || []).filter(s => s.readAt == null).length;
  const myUid = larkleGetUid();
  const pendingRsvps = plans.filter(p =>
    (p.status === 'Open' || p.status === 'Finalized') &&
    (p.invitees || []).some(inv => inv.userId === myUid && inv.rsvp === 'Pending')
  ).length;

  // Each segment badge = things there needing your attention; together they
  // account for the count on the bottom Friends tab.
  const unreadChats = (conversations || []).filter(c => c.unread).length;
  const segments = [
    { id: 'feed',   label: 'Feed',   badge: unreadShares },
    { id: 'chats',  label: 'Chats',  badge: unreadChats },
    { id: 'people', label: 'People', badge: incomingReqs.length },
    { id: 'plans',  label: 'Plans',  badge: pendingRsvps },
  ];

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <div className="hdr">
        <div>
          <h1 style={{ whiteSpace: 'nowrap' }}>The <span className="serif" style={{ color: 'var(--terracotta)' }}>group chat</span></h1>
        </div>
        {isAuthed && (
          <div className="row" style={{ gap: 8 }}>
            <button className="iconbtn" aria-label="Invite people" onClick={() => setInviteOpen(o => !o)}>
              <span style={{ width: 16, height: 16 }}>{Icon.link}</span>
            </button>
            <button className="iconbtn solid" aria-label={tab === 'plans' ? 'New plan' : tab === 'chats' ? 'New chat' : 'Add friend'}
                    onClick={() => tab === 'plans' ? setCreateOpen(true) : tab === 'chats' ? setNewChatOpen(o => !o) : setAddOpen(true)}>+</button>
          </div>
        )}
      </div>

      <div style={{ padding: '0 22px 8px' }}>
        <div style={{ display: 'inline-flex', background: 'rgba(42,26,26,0.06)', borderRadius: 999, padding: 3 }}>
          {segments.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              border: 0, background: tab === t.id ? 'var(--paper)' : 'transparent',
              borderRadius: 999, padding: '6px 14px', fontSize: 12.5, fontWeight: 500,
              color: tab === t.id ? 'var(--ink)' : 'var(--ink-mute)',
              boxShadow: tab === t.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              {t.label}
              {t.badge > 0 && (
                <span style={{
                  minWidth: 15, height: 15, padding: '0 4px', borderRadius: 999,
                  background: 'var(--coral)', color: '#fff',
                  fontSize: 9.5, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>{t.badge > 9 ? '9+' : t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Pick-a-handle nudge — shown until they claim one, since friend
          requests find people by handle. */}
      {isAuthed && !hasHandle && (
        <div style={{
          margin: '0 16px 12px', background: 'var(--paper)', borderRadius: 16,
          padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div className="col" style={{ flex: 1, gap: 1 }}>
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>Pick a handle</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>So friends can find and add you</span>
          </div>
          <button className="btn coral" style={{ height: 34, padding: '0 16px', fontSize: 12.5 }}
                  onClick={onGoToProfile}>
            Choose
          </button>
        </div>
      )}

      {/* Inline panels */}
      {addOpen && (
        <AddFriendPanel
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); reload(); onToast('Request sent'); }}
          onToast={onToast}/>
      )}
      {createOpen && (
        <CreatePlanPanel friends={friends} savedActivities={savedActivities}
          onClose={() => setCreateOpen(false)}
          onCreated={plan => { setPlans(p => [plan, ...p]); setCreateOpen(false); setTab('plans'); onToast('Plan created'); }}
          onToast={onToast}/>
      )}
      {inviteOpen && (
        <InviteCodePanel onClose={() => setInviteOpen(false)} onToast={onToast}/>
      )}
      {newChatOpen && (
        <StartChatPanel friends={friends}
          onClose={() => setNewChatOpen(false)}
          onStarted={convo => { setNewChatOpen(false); onChatStarted(convo); }}
          onToast={onToast}/>
      )}

      <div className="scroll" style={{ padding: '4px 16px 24px' }}>
        {!isAuthed ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8 }}>Sign in to see friends</div>
            <p className="soft" style={{ fontSize: 14 }}>Head to the Profile tab to sign in.</p>
          </div>
        ) : tab === 'feed' ? (
          <>
            {/* Shared with you — sits above the feed; it's the "for you" half */}
            {unreadShares > 0 && (
              <button onClick={onOpenShareInbox} style={{
                width: '100%', border: 0, cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--paper)', borderRadius: 16, padding: '12px 14px',
                marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'left',
              }}>
                <span className="iconbtn solid" style={{ width: 36, height: 36 }}>
                  <span style={{ width: 16, height: 16 }}>{Icon.inbox}</span>
                </span>
                <div className="col" style={{ flex: 1, gap: 1 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                    {unreadShares} new {unreadShares === 1 ? 'place' : 'places'} shared with you
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>Tap to open your inbox</span>
                </div>
                <span style={{ width: 14, height: 14, color: 'var(--ink-mute)', transform: 'rotate(180deg)' }}>
                  <svg viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </button>
            )}

            {friendsActivity == null ? (
              [1, 2].map(i => <FriendActivityCardSkeleton key={i}/>)
            ) : friendsActivity.length === 0 ? (
              friends.length === 0 && !loading ? (
                <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                  <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
                    It's quiet in here
                  </div>
                  <p className="soft" style={{ fontSize: 14, marginBottom: 16 }}>
                    Add some friends and this becomes a feed of the places they've been.
                  </p>
                  <button className="btn coral" style={{ height: 42, padding: '0 24px' }}
                          onClick={() => { setTab('people'); setAddOpen(true); }}>
                    Find friends
                  </button>
                </div>
              ) : (
                <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                  <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8, lineHeight: 1.1 }}>
                    No outings yet
                  </div>
                  <p className="soft" style={{ fontSize: 14 }}>
                    When friends stamp a place done, their postcards land here.
                  </p>
                </div>
              )
            ) : (
              friendsActivity.map(a => (
                <FriendActivityCard key={`${a.friendDid.userId}-${a.id}`} activity={a}
                                    isSaved={savedActivities.some(x => x.id === a.id)}
                                    onOpen={() => onOpenActivity(a.id)}
                                    onSave={() => onSaveActivity(a.id)}/>
              ))
            )}
          </>
        ) : tab === 'chats' ? (
          <ChatsSection conversations={conversations} loading={conversations == null}
                        onOpenChat={onOpenChat}/>
        ) : tab === 'people' ? (
          <PeopleSection
            loading={loading} error={error} reload={reload}
            incomingReqs={incomingReqs} outgoingReqs={outgoingReqs} friends={friends}
            respondToRequest={respondToRequest} cancelRequest={cancelRequest} removeFriend={removeFriend}
            messageFriend={messageFriend}/>
        ) : (
          loading ? (
            [1, 2, 3].map(i => <FriendCardSkeleton key={i}/>)
          ) : plans.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8 }}>No plans yet</div>
              <p className="soft" style={{ fontSize: 14 }}>Tap + to start a group plan with friends.</p>
            </div>
          ) : plans.map(plan => (
            <PlanCard key={plan.id} plan={plan} activities={activities}
                      onOpen={() => onOpenPlan(plan.id)}
                      onOpenActivity={onOpenActivity}
                      onRsvp={rsvp => rsvpPlan(plan.id, rsvp)}
                      onDismiss={() => dismissPlan(plan)}/>
          ))
        )}
      </div>
    </div>
  );
}

// People segment: the management surface — requests in, requests out, and
// the friends list. Kept apart from the feed so browsing and housekeeping
// don't compete.
function PeopleSection({ loading, error, reload, incomingReqs, outgoingReqs, friends, respondToRequest, cancelRequest, removeFriend, messageFriend }) {
  return (
    <>
        {/* Incoming requests */}
        {incomingReqs.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
              {incomingReqs.length} pending {incomingReqs.length === 1 ? 'request' : 'requests'}
            </div>
            {incomingReqs.map(req => {
              const senderName    = req.fromName || null;
              const senderHandle  = req.fromHandle ? `@${req.fromHandle}` : null;
              const senderEmail   = req.fromEmail || null;
              const primaryLabel  = senderName || senderHandle || senderEmail || req.fromUserId;
              const secondaryLabel = senderName && (senderHandle || senderEmail)
                ? (senderHandle || senderEmail)
                : 'wants to be friends';
              return (
              <div key={req.id} style={{
                background: 'var(--paper)', borderRadius: 16, padding: '11px 14px',
                marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}>
                <GenerativeAvatar userId={req.fromUserId} size={38}/>
                <div className="col" style={{ flex: 1, gap: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryLabel}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>{secondaryLabel}</span>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <button className="iconbtn solid" style={{ width: 32, height: 32 }} aria-label="Accept"
                          onClick={() => respondToRequest(req.id, true)}>
                    <span style={{ width: 14, height: 14 }}>{Icon.check}</span>
                  </button>
                  <button className="iconbtn" style={{ width: 32, height: 32 }} aria-label="Decline"
                          onClick={() => respondToRequest(req.id, false)}>
                    <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Outgoing requests */}
        {outgoingReqs.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
              Sent, waiting on them
            </div>
            {outgoingReqs.map(req => {
              const label = req.toName || (req.toHandle ? `@${req.toHandle}` : null) || req.toEmail || req.toUserId;
              return (
                <div key={req.id} style={{
                  background: 'var(--paper)', borderRadius: 16, padding: '10px 14px',
                  marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', opacity: 0.85,
                }}>
                  <GenerativeAvatar userId={req.toUserId || label} size={34}/>
                  <div className="col" style={{ flex: 1, gap: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 500, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>request pending</span>
                  </div>
                  <button className="chip" style={{ height: 28, padding: '0 12px', fontSize: 12, cursor: 'pointer' }}
                          onClick={() => cancelRequest(req.id)}>
                    Cancel
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {loading ? (
          [1, 2, 3].map(i => <FriendCardSkeleton key={i}/>)
        ) : error ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div className="serif" style={{ fontSize: 24, color: 'var(--terracotta)', marginBottom: 8 }}>
              {error === 'offline' ? "You're offline" : "Couldn't load"}
            </div>
            <button className="btn coral" style={{ height: 40, padding: '0 22px' }} onClick={reload}>Try again</button>
          </div>
        ) : friends.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center' }}>
            <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', marginBottom: 8 }}>No friends yet</div>
            <p className="soft" style={{ fontSize: 14 }}>Tap + to find someone by handle or email.</p>
          </div>
        ) : (
          <>
            {(incomingReqs.length > 0 || outgoingReqs.length > 0) && (
              <div className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
                {friends.length} {friends.length === 1 ? 'friend' : 'friends'}
              </div>
            )}
            {friends.map(f => (
              <FriendCard key={f.userId} friend={f} onRemove={() => removeFriend(f.userId)}
                          onMessage={() => messageFriend(f.userId)}/>
            ))}
          </>
        )}
    </>
  );
}

function FriendCard({ friend, onRemove, onMessage }) {
  const [confirming, setConfirming] = React.useState(false);
  return (
    <div style={{
      background: confirming ? 'rgba(196,68,68,0.07)' : 'var(--paper)',
      borderRadius: 18, padding: 12, marginBottom: 10,
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'background 0.18s',
    }}>
      <FriendAvatar friend={friend} size={42}/>
      {confirming ? (
        <>
          <div className="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Remove {friend.name}?</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>They won't be notified</span>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button onClick={onRemove} style={{
              height: 30, padding: '0 12px', border: 0,
              borderRadius: 8, background: '#c44', color: '#fff',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>Remove</button>
            <button onClick={() => setConfirming(false)} style={{
              height: 30, padding: '0 12px', border: '0.5px solid var(--hair)',
              borderRadius: 8, background: 'var(--paper)', color: 'var(--ink)',
              fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="col" style={{ flex: 1, gap: 2, minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>{friend.name}</span>
            {friend.handle && <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>@{friend.handle}</span>}
          </div>
          <button className="iconbtn" style={{ width: 30, height: 30 }} aria-label={`Message ${friend.name}`}
                  onClick={onMessage}>
            <span style={{ width: 14, height: 14 }}>{Icon.msg}</span>
          </button>
          <button className="iconbtn" style={{ width: 30, height: 30 }} aria-label="Remove"
                  onClick={() => setConfirming(true)}>
            <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
          </button>
        </>
      )}
    </div>
  );
}

export function PlanCard({ plan, activities, onOpen, onOpenActivity, onRsvp, onDismiss }) {
  const [confirming, setConfirming] = React.useState(false);
  const uid = larkleGetUid();
  const isHost = plan.organizerId === uid;
  const myInvitee = plan.invitees?.find(inv => inv.userId === uid);
  const myRsvp = myInvitee?.rsvp;
  // Finalized = place locked, attendance still open — only Cancelled/Completed close RSVPs
  const canRsvp = plan.status === 'Open' || plan.status === 'Finalized';
  const yesCount  = (plan.invitees || []).filter(i => i.rsvp === 'Yes').length;
  const maybeCount = (plan.invitees || []).filter(i => i.rsvp === 'Maybe').length;
  const pendingCount = (plan.invitees || []).filter(i => i.rsvp === 'Pending').length;

  // Resolve the finalized place: try the loaded deck first, fall back to a
  // direct location fetch so finalized places always render.
  const deckPlace = plan.finalPlaceId ? activities.find(a => a.id === plan.finalPlaceId) : null;
  const [fetchedPlace, setFetchedPlace] = React.useState(null);
  React.useEffect(() => {
    if (!plan.finalPlaceId || deckPlace) { setFetchedPlace(null); return; }
    let alive = true;
    apiFetchLocation(plan.finalPlaceId)
      .then(p => { if (alive && p) setFetchedPlace(placeToActivity(p)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [plan.finalPlaceId, !!deckPlace]);
  const place = deckPlace || fetchedPlace;

  const scheduledStr = plan.scheduledFor
    ? new Date(plan.scheduledFor * 1000).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric',
      })
    : null;

  const statusChip = { Open: '#E8A33C', Finalized: '#5c7a4a', Cancelled: '#c44', Completed: '#888' }[plan.status] || '#888';

  return (
    <div onClick={onOpen} style={{ background: 'var(--paper)', borderRadius: 22, padding: 14, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: onOpen ? 'pointer' : 'default' }}>
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="serif" style={{ fontSize: 20, color: 'var(--ink)', lineHeight: 1.1, flex: 1, marginRight: 10 }}>{plan.title}</span>
        <div className="row" style={{ gap: 6, flexShrink: 0 }}>
          {plan.status !== 'Finalized' && (
            <span style={{
              fontSize: 10.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
              background: statusChip + '28', color: statusChip, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>{plan.status}</span>
          )}
          {onDismiss && (
            <button className="iconbtn" style={{ width: 26, height: 26 }} aria-label={isHost ? 'Cancel plan' : 'Leave plan'}
                    onClick={e => { e.stopPropagation(); setConfirming(true); }}>
              <span style={{ width: 11, height: 11 }}>{Icon.x}</span>
            </button>
          )}
        </div>
      </div>

      {confirming ? (
        <div className="col" style={{ gap: 10 }}>
          <span style={{ fontSize: 13.5, color: 'var(--ink)' }}>
            {isHost ? 'Cancel this plan for everyone?' : 'Leave this plan?'}
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button onClick={e => { e.stopPropagation(); onDismiss(); }} style={{
              height: 34, padding: '0 14px', border: 0,
              borderRadius: 10, background: '#c44', color: '#fff',
              fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            }}>{isHost ? 'Cancel plan' : 'Leave plan'}</button>
            <button onClick={e => { e.stopPropagation(); setConfirming(false); }} style={{
              height: 34, padding: '0 14px', border: '0.5px solid var(--hair)',
              borderRadius: 10, background: 'var(--paper)', color: 'var(--ink)',
              fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
            }}>Never mind</button>
          </div>
        </div>
      ) : (
      <>
      {scheduledStr && (
        <div className="row" style={{ gap: 6, marginBottom: 8, fontSize: 12.5, color: 'var(--ink-mute)' }}>
          <span style={{ width: 13, height: 13 }}>{Icon.cal}</span>
          {scheduledStr}
        </div>
      )}

      {place ? (
        <div onClick={e => { e.stopPropagation(); onOpenActivity(place); }} style={{
          display: 'flex', gap: 10, marginBottom: 12, cursor: 'pointer',
          background: 'var(--bg-tint)', borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{ width: 72, flexShrink: 0 }}>
            <HeroArt activity={place} height={72} rounded={0} showNav={false}/>
          </div>
          <div className="col" style={{ justifyContent: 'center', padding: '0 8px 0 0', gap: 3 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{place.name}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>{place.address || place.kind}</span>
          </div>
        </div>
      ) : plan.finalPlaceId ? (
        // Same footprint as the resolved card above — holds the layout still
        // while the deck-miss fallback fetch (apiFetchLocation) is in flight,
        // instead of the card popping in and shoving the RSVP row down.
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, background: 'var(--bg-tint)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ width: 72, height: 72, flexShrink: 0, background: 'var(--paper)', animation: 'larkle-shimmer 1.4s ease-in-out infinite' }}/>
          <div className="col" style={{ justifyContent: 'center', padding: '0 8px 0 0', gap: 6, flex: 1 }}>
            <div style={{ height: 12, width: '60%', borderRadius: 6, background: 'var(--paper)', animation: 'larkle-shimmer 1.4s ease-in-out infinite' }}/>
            <div style={{ height: 10, width: '40%', borderRadius: 6, background: 'var(--paper)', animation: 'larkle-shimmer 1.4s ease-in-out 0.1s infinite' }}/>
          </div>
        </div>
      ) : null}

      <div className="row" style={{ gap: 14, marginBottom: myRsvp !== undefined && canRsvp ? 12 : 0, fontSize: 12.5, color: 'var(--ink-mute)' }}>
        {yesCount > 0   && <span><b style={{ color: 'var(--ink)' }}>{yesCount}</b> in</span>}
        {maybeCount > 0 && <span><b style={{ color: 'var(--ink)' }}>{maybeCount}</b> maybe</span>}
        {pendingCount > 0 && <span><b style={{ color: 'var(--ink)' }}>{pendingCount}</b> pending</span>}
      </div>

      {myRsvp !== undefined && canRsvp && (
        <div className="row" style={{ gap: 8 }}>
          {[['Yes', "I'm in"], ['Maybe', 'Maybe'], ['No', "Can't"]].map(([val, label]) => (
            <button key={val} onClick={e => { e.stopPropagation(); onRsvp(val); }} style={{
              flex: 1, height: 34, border: 0, borderRadius: 10, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
              background: myRsvp === val ? 'var(--ink)' : 'rgba(42,26,26,0.07)',
              color: myRsvp === val ? 'var(--paper)' : 'var(--ink)',
            }}>{label}</button>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

function AddFriendPanel({ onClose, onAdded, onToast }) {
  const [query, setQuery] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [result, setResult] = React.useState(undefined); // undefined = not searched, null = no result
  const [sending, setSending] = React.useState(false);

  const doSearch = () => {
    if (!query.trim()) return;
    setSearching(true);
    setResult(undefined);
    apiSearchUser(query.trim())
      .then(r => setResult(r || null))
      .catch(() => { onToast('Search failed'); setResult(null); })
      .finally(() => setSearching(false));
  };

  const sendRequest = () => {
    if (!result) return;
    setSending(true);
    const isEmail = query.includes('@') && !query.startsWith('@');
    apiSendFriendRequest(isEmail ? { email: query.trim() } : { handle: query.trim().replace(/^@/, '') })
      .then(() => { trackEvent('friend_request_sent', { method: isEmail ? 'email' : 'handle' }); onAdded(); })
      .catch(() => onToast('Could not send request'))
      .finally(() => setSending(false));
  };

  return (
    <div style={{ margin: '0 16px 12px', background: 'var(--paper)', borderRadius: 18, padding: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div className="row between" style={{ marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Find someone</span>
        <button className="iconbtn" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28 }}>
          <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
        </button>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <input value={query}
               onChange={e => { setQuery(e.target.value); setResult(undefined); }}
               onKeyDown={e => e.key === 'Enter' && doSearch()}
               placeholder="@handle or email"
               aria-label="Search by handle or email"
               style={{
                 flex: 1, height: 36, border: '0.5px solid var(--hair)', borderRadius: 10,
                 padding: '0 12px', fontFamily: 'inherit', fontSize: 13.5,
                 background: 'var(--bg)', outline: 'none', color: 'var(--ink)',
               }}/>
        <button className="btn coral" style={{ height: 36, padding: '0 16px', fontSize: 13 }}
                onClick={doSearch} disabled={searching}>
          {searching ? '...' : 'Search'}
        </button>
      </div>

      {result && (
        <div className="row between" style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg-tint)', borderRadius: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <GenerativeAvatar userId={result.userId || result.name || 'unknown'} size={36}/>
            <div className="col" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{result.name}</span>
              {result.handle && <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>@{result.handle}</span>}
            </div>
          </div>
          <button className="btn coral" style={{ height: 32, padding: '0 14px', fontSize: 12.5 }}
                  onClick={sendRequest} disabled={sending}>
            {sending ? '...' : 'Add'}
          </button>
        </div>
      )}

      {result === null && !searching && (
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--ink-mute)', textAlign: 'center', padding: '6px 0' }}>
          No one found. Check the handle or email
        </div>
      )}
    </div>
  );
}

// Invite people who aren't on the app yet: generate a code, share the link.
function InviteCodePanel({ onClose, onToast }) {
  const [code, setCode] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    apiGenerateInviteCode()
      .then(r => setCode(r?.code || r?.inviteCode || (typeof r === 'string' ? r : null)))
      .catch(() => setError(true));
  }, []);

  const link = code ? `${window.location.origin}/invite/${code}` : null;

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      onToast('Invite link copied');
    } catch {
      onToast('Could not copy. Long-press to copy');
    }
  };

  const share = async () => {
    if (!link) return;
    if (navigator.share) {
      try { await navigator.share({ title: 'Join me on Larkle', url: link }); } catch {}
    } else {
      copy();
    }
  };

  return (
    <div style={{ margin: '0 16px 12px', background: 'var(--paper)', borderRadius: 18, padding: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div className="row between" style={{ marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Invite someone new</span>
        <button className="iconbtn" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28 }}>
          <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
        </button>
      </div>
      {error ? (
        <div style={{ fontSize: 13, color: 'var(--ink-mute)', padding: '6px 0' }}>Could not create an invite link. Try again later.</div>
      ) : !code ? (
        <div style={{ fontSize: 13, color: 'var(--ink-mute)', padding: '6px 0' }}>Creating your invite link…</div>
      ) : (
        <>
          <div style={{
            padding: '10px 12px', borderRadius: 12, marginBottom: 10,
            background: 'var(--bg-tint)', border: '0.5px solid var(--hair)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {link}
            </span>
            <button className="chip coral" style={{ cursor: 'pointer', height: 28, padding: '0 12px', flexShrink: 0 }} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn ghost" style={{ flex: 1, height: 42 }} onClick={share}>
              <span style={{ width: 17, height: 17 }}>{Icon.share}</span> Share
            </button>
          </div>
          <p className="soft" style={{ fontSize: 11.5, margin: '10px 0 0' }}>
            When they sign in through this link you'll be friends automatically.
          </p>
        </>
      )}
    </div>
  );
}

function CreatePlanPanel({ friends, savedActivities = [], onClose, onCreated, onToast, initialPlaceId }) {
  const [title, setTitle] = React.useState('');
  const [date, setDate] = React.useState('');
  const [placeId, setPlaceId] = React.useState(initialPlaceId || null);
  const [invitees, setInvitees] = React.useState([]);
  const [creating, setCreating] = React.useState(false);

  const toggle = (uid) => setInvitees(s => s.includes(uid) ? s.filter(id => id !== uid) : [...s, uid]);
  const chosenPlace = placeId ? savedActivities.find(a => a.id === placeId) : null;
  // The place names the plan unless the user names it themselves
  const effectiveTitle = title.trim() || (chosenPlace ? `${chosenPlace.name}?` : '');

  const create = () => {
    if (!effectiveTitle) return;
    if (effectiveTitle.length > 120) { onToast('Title is too long (max 120)'); return; }
    if (invitees.length > 25) { onToast('Max 25 invitees per plan'); return; }
    setCreating(true);
    apiCreatePlan({
      title: effectiveTitle,
      inviteeIds: invitees,
      ...(date ? { scheduledFor: Math.floor(new Date(date).getTime() / 1000) } : {}),
      ...(placeId ? { finalPlaceId: placeId } : {}),
    })
      .then(plan => {
        trackEvent('plan_created', { source: 'friends_tab', invitee_count: invitees.length, has_place: !!placeId });
        onCreated(plan);
      })
      .catch(() => onToast('Could not create plan'))
      .finally(() => setCreating(false));
  };

  return (
    <div style={{ margin: '0 16px 12px', background: 'var(--paper)', borderRadius: 18, padding: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div className="row between" style={{ marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>New plan</span>
        <button className="iconbtn" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28 }}>
          <span style={{ width: 12, height: 12 }}>{Icon.x}</span>
        </button>
      </div>

      {/* Where to — plans start from a place */}
      <div className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
        Where to?
      </div>
      {savedActivities.length === 0 ? (
        <p className="soft" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
          Nothing saved yet. Swipe right on Discover to collect spots, or make an open plan and pick later.
        </p>
      ) : (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 10, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {savedActivities.map(a => {
            const sel = placeId === a.id;
            return (
              <button key={a.id} onClick={() => setPlaceId(sel ? null : a.id)}
                      aria-label={sel ? `Deselect ${a.name}` : `Choose ${a.name}`}
                      style={{
                        width: 104, flexShrink: 0, border: 0, padding: 0, cursor: 'pointer',
                        fontFamily: 'inherit', background: 'transparent', textAlign: 'left',
                        borderRadius: 14,
                        overflow: 'hidden', position: 'relative',
                      }}>
                <HeroArt activity={a} height={58} rounded={0} showNav={false}/>
                {/* ring drawn above the photo, inset so the scroll clip can't cut it */}
                <span aria-hidden style={{
                  position: 'absolute', inset: 0, borderRadius: 14, pointerEvents: 'none',
                  boxShadow: sel ? 'inset 0 0 0 2.5px var(--coral)' : 'inset 0 0 0 0.5px var(--hair)',
                }}/>
                {sel && (
                  <span style={{
                    position: 'absolute', top: 5, right: 5, width: 20, height: 20,
                    borderRadius: '50%', background: 'var(--coral)', color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ width: 11, height: 11 }}>{Icon.check}</span>
                  </span>
                )}
                <span style={{
                  display: 'block', padding: '5px 7px 7px', fontSize: 11, fontWeight: 500,
                  lineHeight: 1.2, color: 'var(--ink)', background: 'var(--bg-tint)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{a.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <input value={title} onChange={e => setTitle(e.target.value)}
             placeholder={chosenPlace ? `${chosenPlace.name}?` : 'Give it a name'}
             maxLength={120} aria-label="Plan title"
             style={{
               width: '100%', height: 38, border: '0.5px solid var(--hair)', borderRadius: 10,
               padding: '0 12px', fontFamily: 'inherit', fontSize: 14, background: 'var(--bg)',
               outline: 'none', color: 'var(--ink)', marginBottom: 10, boxSizing: 'border-box',
             }}/>
      <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
             aria-label="When"
             style={{
               width: '100%', height: 36, border: '0.5px solid var(--hair)', borderRadius: 10,
               padding: '0 12px', fontFamily: 'inherit', fontSize: 13, background: 'var(--bg)',
               outline: 'none', color: 'var(--ink)', marginBottom: friends.length ? 12 : 14, boxSizing: 'border-box',
             }}/>
      {friends.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>Invite friends</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {friends.map(f => {
              const sel = invitees.includes(f.userId);
              const displayName = f.name || f.handle || f.userId.slice(0, 8);
              return (
                <button key={f.userId} onClick={() => toggle(f.userId)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                  borderRadius: 999, border: 0, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 12.5, fontWeight: 500,
                  background: sel ? 'var(--ink)' : 'rgba(42,26,26,0.07)',
                  color: sel ? 'var(--paper)' : 'var(--ink)',
                }}>
                  <FriendAvatar friend={f} size={20}/>
                  {displayName.split(' ')[0]}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <button className="btn coral full" style={{ height: 38 }} onClick={create} disabled={creating || !effectiveTitle}>
        {creating ? 'Creating...' : chosenPlace ? `Plan it at ${chosenPlace.name.length > 22 ? chosenPlace.name.slice(0, 22) + '…' : chosenPlace.name}` : 'Create plan'}
      </button>
    </div>
  );
}

// ── Profile ────────────────────────────────────────────────────────────────
export function ProfileScreen({ authUser, doneEntries, savedActivities = [], vibe, onSignOut, onSignIn, onDeleteAccount, onToast, onRefreshConversations }) {
  const fbUser = larkleGetUser();

  const displayName = fbUser?.displayName || fbUser?.email?.split('@')[0] || 'You';
  const email       = fbUser?.email || '';
  const phone       = fbUser?.phoneNumber || '';
  const initials    = displayName.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase() || '?';

  // Per-uid cache for instant paint; the server profile is the source of
  // truth and corrects it below (fresh devices, cleared storage).
  const handleCacheKey = `larkle-handle:${larkleGetUid() || 'anon'}`;
  const [handle, setHandle] = React.useState(() => localStorage.getItem(handleCacheKey) || '');
  const [input, setInput]   = React.useState(handle);

  React.useEffect(() => {
    if (!authUser) return;
    let alive = true;
    apiGetProfile()
      .then(p => {
        if (!alive || !p?.handle) return;
        setHandle(p.handle);
        setInput(prev => prev || p.handle);
        try { localStorage.setItem(handleCacheKey, p.handle); } catch {}
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [authUser]);
  const [status, setStatus] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [pushOn, setPushOn] = React.useState(pushEnabled());
  const [pushBusy, setPushBusy] = React.useState(false);
  const [canInstall, setCanInstall] = React.useState(installAvailable());
  const checkRef            = React.useRef(null);

  React.useEffect(() => onInstallAvailabilityChange(setCanInstall), []);

  const installApp = () => {
    promptInstall().then(res => {
      if (res.ok) onToast('Installed. Open it from your home screen');
      setCanInstall(installAvailable());
    });
  };

  const turnOnPush = () => {
    setPushBusy(true);
    enablePush(larkleGetApp(), (msg) => {
      const n = msg?.notification;
      if (n?.title) onToast(n.title);
      // New chat message while the thread isn't open — the chat list/unread
      // badge only otherwise refresh on a 60s poll or window focus, so pull
      // it immediately or the new message sits invisible until then.
      if (msg?.data?.type === 'chatMessage') onRefreshConversations?.();
    })
      .then(res => {
        if (res.ok) { setPushOn(true); onToast('Notifications on'); }
        else if (res.reason === 'denied') onToast('Notifications blocked in browser settings');
        else onToast('Push not available here');
      })
      .catch(() => onToast('Could not enable notifications'))
      .finally(() => setPushBusy(false));
  };

  const onInputChange = (v) => {
    const clean = v.toLowerCase().replace(/[^a-z0-9_.]/g, '');
    setInput(clean);
    setStatus(null);
    if (!clean || clean === handle) return;
    clearTimeout(checkRef.current);
    setStatus('checking');
    checkRef.current = setTimeout(() => {
      apiCheckHandle(clean)
        .then(r => setStatus(r.available ? 'available' : 'taken'))
        .catch(() => setStatus(null));
    }, 500);
  };

  const saveHandle = () => {
    if (status !== 'available' || !input) return;
    setSaving(true);
    apiClaimHandle(input)
      .then(r => {
        if (r.claimed) {
          setHandle(input);
          localStorage.setItem(handleCacheKey, input);
          setStatus(null);
          onToast('Handle saved');
        } else {
          setStatus('taken');
        }
      })
      .catch(() => onToast('Could not save handle'))
      .finally(() => setSaving(false));
  };

  const statusColor = { available: 'var(--green)', taken: 'var(--red)', checking: 'var(--ink-mute)' }[status] || 'var(--hair)';

  const doneCount  = doneEntries.length;
  const savedCount = savedActivities.length;
  const avgRating  = doneCount
    ? (doneEntries.reduce((s, e) => s + (e.stars || 0), 0) / doneCount).toFixed(1)
    : null;

  if (!authUser) {
    return (
      <div className="screen" style={{ background: 'var(--bg)' }}>
        <div className="hdr">
          <div>
            <div className="eyebrow">Account</div>
            <h1>Your <span className="serif" style={{ color: 'var(--terracotta)' }}>profile</span></h1>
          </div>
        </div>
        <div className="scroll" style={{
          padding: '48px 24px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', textAlign: 'center', gap: 18,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(42,26,26,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" stroke="var(--ink-mute)" strokeWidth="1.6"/>
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="var(--ink-mute)" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="col" style={{ gap: 8, alignItems: 'center' }}>
            <div className="serif" style={{ fontSize: 26, color: 'var(--terracotta)', lineHeight: 1.1 }}>Sign in to sync</div>
            <p className="soft" style={{ fontSize: 14, maxWidth: 260, margin: 0 }}>
              Save your vibe, see saved spots across devices, and plan nights out with friends.
            </p>
          </div>
          <button className="btn coral" style={{ height: 46, padding: '0 36px', fontSize: 15, marginTop: 8 }}
                  onClick={onSignIn}>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <div className="hdr">
        <div>
          <div className="eyebrow">Account</div>
          <h1>Your <span className="serif" style={{ color: 'var(--terracotta)' }}>profile</span></h1>
        </div>
      </div>

      <div className="scroll" style={{ padding: '12px 18px 80px' }}>
        {/* Identity card */}
        <div style={{
          background: 'var(--paper)', borderRadius: 22, padding: '18px 16px',
          marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 58, height: 58, borderRadius: '50%', background: 'var(--coral)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 700, flexShrink: 0, letterSpacing: '-0.02em',
          }}>{initials}</div>
          <div className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
            {handle && <span style={{ fontSize: 12, color: 'var(--terracotta)' }}>@{handle}</span>}
            {email  && <span style={{ fontSize: 12, color: 'var(--ink-mute)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</span>}
            {phone  && !email && <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{phone}</span>}
          </div>
        </div>

        {/* Stats strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
          {[
            { label: 'Saved', value: savedCount },
            { label: 'Done',  value: doneCount  },
            { label: 'Avg ★', value: avgRating || '-' },
          ].map(({ label, value }) => (
            <div key={label} style={{
              background: 'var(--paper)', borderRadius: 16, padding: '14px 8px',
              textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <div className="serif" style={{ fontSize: 28, lineHeight: 1, color: 'var(--ink)' }}>{value}</div>
              <div className="upper" style={{ fontSize: 9, color: 'var(--ink-mute)', fontWeight: 600, marginTop: 5 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Vibe snapshot */}
        <div style={{
          background: 'var(--paper)', borderRadius: 18, padding: '16px',
          marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 10 }}>
            Your vibe snapshot
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Constellation user={vibe} activity={{ vibe }} size={168}/>
          </div>
        </div>

        {/* Handle */}
        <div style={{
          background: 'var(--paper)', borderRadius: 18, padding: '16px',
          marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div className="upper" style={{ fontSize: 9.5, color: 'var(--ink-mute)', fontWeight: 600, marginBottom: 8 }}>
            Handle
          </div>
          <div className="row" style={{ gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{
                position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--ink-mute)', fontSize: 14, pointerEvents: 'none',
              }}>@</span>
              <input value={input} onChange={e => onInputChange(e.target.value)}
                     onKeyDown={e => e.key === 'Enter' && saveHandle()}
                     placeholder={handle || 'choose a handle'}
                     aria-label="Handle"
                     style={{
                       width: '100%', height: 38, boxSizing: 'border-box',
                       border: `0.5px solid ${statusColor}`,
                       borderRadius: 10, paddingLeft: 26, paddingRight: 12,
                       fontFamily: 'inherit', fontSize: 13.5,
                       background: 'var(--bg)', outline: 'none', color: 'var(--ink)',
                     }}/>
            </div>
            <button className="btn coral" style={{ height: 38, padding: '0 16px', fontSize: 13 }}
                    onClick={saveHandle}
                    disabled={status !== 'available' || saving || !input}>
              {saving ? '...' : 'Save'}
            </button>
          </div>
          {status === 'available' && <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 5 }}>@{input} is available</div>}
          {status === 'taken'     && <div style={{ fontSize: 12, color: 'var(--red)',   marginTop: 5 }}>@{input} is already taken</div>}
          {status === 'checking'  && <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 5 }}>Checking...</div>}
        </div>

        {/* Install app */}
        {canInstall && !isStandalone() && (
          <div style={{
            background: 'var(--paper)', borderRadius: 18, padding: '14px 16px',
            marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div className="col" style={{ flex: 1, gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>Install app</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                Adds Larkle to your home screen, full-screen, no browser bar
              </span>
            </div>
            <button className="btn coral" style={{ height: 34, padding: '0 16px', fontSize: 12.5 }}
                    onClick={installApp}>
              Install
            </button>
          </div>
        )}

        {/* Notifications */}
        {pushSupported() && (
          <div style={{
            background: 'var(--paper)', borderRadius: 18, padding: '14px 16px',
            marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div className="col" style={{ flex: 1, gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>Notifications</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                Friend requests, plan invites, and shares
              </span>
            </div>
            {pushOn ? (
              <span className="chip green" style={{ height: 28, padding: '0 12px' }}>On</span>
            ) : (
              <button className="btn coral" style={{ height: 34, padding: '0 16px', fontSize: 12.5 }}
                      onClick={turnOnPush} disabled={pushBusy}>
                {pushBusy ? '...' : 'Turn on'}
              </button>
            )}
          </div>
        )}

        {/* Sign out */}
        <button onClick={onSignOut} style={{
          width: '100%', height: 44, border: 0, borderRadius: 12, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
          background: 'rgba(42,26,26,0.07)', color: 'var(--ink)',
          marginBottom: 10,
        }}>
          Sign out
        </button>

        {/* Delete account */}
        {confirmingDelete ? (
          <div style={{
            background: 'rgba(178,58,42,0.06)', border: '1px solid rgba(178,58,42,0.25)',
            borderRadius: 14, padding: 14,
          }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Delete your account?</div>
            <p className="soft" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
              This permanently removes your preferences, saved places, reviews, friendships,
              and plans. It cannot be undone.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button onClick={() => {
                setDeleting(true);
                onDeleteAccount().finally(() => setDeleting(false));
              }} disabled={deleting} style={{
                flex: 1, height: 38, border: 0, borderRadius: 10, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                background: 'var(--red)', color: '#fff',
              }}>{deleting ? 'Deleting…' : 'Yes, delete everything'}</button>
              <button onClick={() => setConfirmingDelete(false)} disabled={deleting} style={{
                flex: 1, height: 38, border: '0.5px solid var(--hair)', borderRadius: 10, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                background: 'var(--paper)', color: 'var(--ink)',
              }}>Keep my account</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmingDelete(true)} style={{
            width: '100%', height: 40, border: 0, borderRadius: 12, cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
            background: 'transparent', color: 'var(--red)',
          }}>
            Delete account
          </button>
        )}
      </div>
    </div>
  );
}

// ── Share inbox ────────────────────────────────────────────────────────────
// Places friends sent you. Opening marks visible shares read.
export function ShareInboxSheet({ shares, onClose, onOpenActivity, onSaveShare, onToast, onMarkedRead }) {
  React.useEffect(() => {
    const unread = (shares || []).filter(s => s.readAt == null).map(s => s.id);
    if (unread.length) {
      apiMarkSharesRead(unread).then(() => onMarkedRead?.(unread)).catch(() => {});
    }
  }, []);

  return (
    <>
      <div className="scrim" onClick={onClose}/>
      <div className="sheet" role="dialog" aria-label="Shared with you">
        <div className="handle"/>
        <div className="row between" style={{ marginBottom: 12 }}>
          <h3 className="serif" style={{ fontSize: 26, margin: 0, color: 'var(--ink)' }}>
            Shared <span style={{ color: 'var(--terracotta)' }}>with you</span>
          </h3>
          <button className="iconbtn" onClick={onClose} aria-label="Close">
            <span style={{ width: 16, height: 16 }}>{Icon.x}</span>
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {(shares || []).length === 0 ? (
            <div style={{ padding: '40px 12px', textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 22, color: 'var(--terracotta)', marginBottom: 6 }}>Nothing yet</div>
              <p className="soft" style={{ fontSize: 13 }}>When friends send you places, they land here.</p>
            </div>
          ) : shares.map(s => {
            const activity = s.activity; // pre-mapped by App
            const fromLabel = s.fromName || (s.fromHandle ? `@${s.fromHandle}` : 'A friend');
            return (
              <div key={s.id} style={{
                background: 'var(--bg-tint)', borderRadius: 16, padding: 10,
                marginBottom: 10, border: s.readAt == null ? '1px solid var(--coral)' : '0.5px solid var(--hair)',
              }}>
                <div className="row" style={{ gap: 8, marginBottom: 8 }}>
                  <GenerativeAvatar userId={s.fromUserId} size={24}/>
                  <span style={{ fontSize: 12.5 }}>
                    <b>{fromLabel}</b> sent you this{s.note ? ':' : ''}
                  </span>
                </div>
                {s.note && (
                  <p className="serif" style={{ fontSize: 15, color: 'var(--ink)', margin: '0 0 8px', lineHeight: 1.3 }}>
                    “{s.note}”
                  </p>
                )}
                {activity && (
                  <div onClick={() => onOpenActivity(activity.id)} style={{
                    display: 'flex', gap: 10, cursor: 'pointer',
                    background: 'var(--paper)', borderRadius: 12, overflow: 'hidden',
                  }}>
                    <div style={{ width: 64, flexShrink: 0 }}>
                      <HeroArt activity={activity} height={64} rounded={0} showNav={false}/>
                    </div>
                    <div className="col" style={{ justifyContent: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activity.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-mute)' }}>{activity.kind}</span>
                    </div>
                    <button className="chip coral" style={{ cursor: 'pointer', height: 26, padding: '0 11px', alignSelf: 'center', marginRight: 8, flexShrink: 0 }}
                            onClick={e => { e.stopPropagation(); onSaveShare(activity); }}>
                      Save
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
